import { useEffect, useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { SubPage } from "../components/layout.tsx";
import {
  Badge,
  Button,
  Empty,
  ErrorBlock,
  Img,
  Input,
  Price,
  SectionTitle,
  Spinner,
} from "../components/ui.tsx";
import { Card } from "@/components/ui/card";
import { ImageUpload } from "../components/image-upload.tsx";
import { useCategories, useIdentities } from "../api/queries.ts";
import { orgFetch } from "../api/org-client.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopMyProducts } from "./desktop/my-products.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  我的商品
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **商家也要能上架自己的货。**
 *    装修师傅手上常有自己进的辅料、自己做的定制柜 ——
 *    以前只有供应商和经销商能上架，而且**只有桌面版界面**，
 *    分销商在手机上一件自己的商品都传不了，只能把客户引到微信去成交。
 *
 * ⚠️ 上架的是**他自己的**商品（`supplierOrgId` 就是他），
 *    价格链从他这一级起算 —— 上级不在他自己的货上抽差价。
 *
 * ⚠️ 跟「我的定价」分开：那一页管的是**上级供的货我卖多少**，
 *    这一页管的是**我自己的货**。两件事，两个页面。
 */

export interface MyProduct {
  id: number;
  title: string;
  status: string;
  /**
   * ⚠️ **服务端转好的地址**，不是对象存储的 key。
   *    库里存的是 `products/2026-08/xxx.jpg`，前端拼不出地址 ——
   *    早先这里读的是不存在的 `fromPrice`/裸 key，真机上满屏破图。
   */
  imageUrls?: string[];
  /** 我的成本（最低那个规格的供货价）。⚠️ 对下级和客户永远不可见。 */
  cost?: number | null;
  skuCount?: number;
  stock?: number;
}

export const STATUS_LABEL: Record<string, string> = {
  draft: "草稿",
  pending: "待审核",
  active: "在架",
  rejected: "已驳回",
  offline: "已下架",
};

export const STATUS_TONE: Record<string, "secondary" | "warning" | "success" | "destructive"> = {
  draft: "secondary",
  pending: "warning",
  active: "success",
  rejected: "destructive",
  offline: "secondary",
};

/**
 * 新建商品。
 *
 * 🔴 **一步到位：商品信息 + 第一个规格一起填。**
 *    分成"先建商品、再加规格、再提交审核"三步的话，手机上很容易停在中间 ——
 *    建了个没有规格的商品，既卖不了也不知道差什么。
 */
function NewProduct({
  orgId,
  orgType,
  onDone,
}: {
  orgId: number;
  /**
   * 🔴 **同一个字段，两种身份的含义完全不同。**
   *    - **供应商和平台**填的是**供货价**：他们是源头，
   *      下游各级要在它之上加自己的差价
   *    - **经销商/分销商**填的是**他自己的售价**：货是他自己的，
   *      客户看到的就是这个数（服务端会把成本和售价设成同一个）
   *    标错一个字，供应商会把零售价当供货价填进去，全链的价当场翻倍。
   *
   * ⚠️ 平台归到"源头"那一档：陈总上自营的货时填的是成本，
   *    他自己的两个售价（给下级的批发价 / 店里卖给消费者的价）
   *    在「我的定价」里设，不在这张表单上。
   */
  orgType: string;
  onDone: () => void;
}) {
  const isSupplier = orgType === "supplier" || orgType === "platform";
  const { data: categories } = useCategories();
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [imageKeys, setImageKeys] = useState<string[]>([]);
  const [detailKeys, setDetailKeys] = useState<string[]>([]);
  const [spec, setSpec] = useState("标准装");
  /** ⚠️ 输入的是元，接口要分 —— 换算只做一次。 */
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("");
  const [unit, setUnit] = useState("件");


  const create = useMutation({
    mutationFn: async () => {
      const product = await orgFetch<{ id: number }>("/products", orgId, {
        method: "POST",
        body: JSON.stringify({
          title: title.trim(),
          categoryId,
          imageKeys,
          detailImageKeys: detailKeys,
        }),
      });
      await orgFetch("/skus", orgId, {
        method: "POST",
        body: JSON.stringify({
          productId: product.id,
          specs: { 规格: spec.trim() || "标准装" },
          /**
           * 🔴 这里填的是**我的成本价**（供货价）—— 链条的起点。
           *    我卖多少在「我的定价」里定，或者按批量加价率算。
           */
          /**
           * 🔴 **他填的是"我卖多少"，也就是客户看到的价。**
           *    货是他自己的，没有"进货成本"这一说 —— 服务端会把这一级的
           *    成本和售价设成同一个数（见 `catalogService.addSku`）。
           */
          supplyPrice: Math.round(Number(price) * 100),
          stock: Number(stock || 0),
          unit: unit.trim() || "件",
        }),
      });
      /** ⚠️ 建完直接提交审核 —— 停在草稿里的商品谁也卖不了。 */
      await orgFetch(`/products/${product.id}/submit`, orgId, { method: "POST" });
      return product;
    },
    onSuccess: onDone,
  });

  const ready =
    title.trim().length >= 2 && categoryId && imageKeys.length > 0 && Number(price) > 0;

  return (
    <div className="space-y-3 px-3 py-3 pb-8">
      <SectionTitle>商品信息</SectionTitle>
      <Card className="space-y-2.5 px-4 py-4">
        <Input
          label="商品名称"
          placeholder="例如：圣象强化复合地板 暖橡"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <div>
          <span className="mb-1.5 block text-[13px] text-muted-foreground">品类</span>
          {/*
            ⚠️ 这里选的是**平台的全站品类**（瓷砖/地板/灯具），
               不是店铺自己的分类 —— 前者决定客户在分类页能不能搜到它，
               后者是他自己店里的货架。两个都要，但不是一回事。
          */}
          <select
            className="w-full rounded-lg border border-border bg-card px-3 py-2.5 text-[16px]"
            value={categoryId ?? ""}
            onChange={(e) => setCategoryId(Number(e.target.value) || null)}
          >
            <option value="">请选择</option>
            {(categories ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <span className="mb-1.5 block text-[13px] text-muted-foreground">
            商品图（至少一张）
          </span>
          <ImageUpload value={imageKeys} onChange={setImageKeys} scope="product" orgId={orgId} max={6} />
        </div>
      </Card>

      <SectionTitle>规格与价格</SectionTitle>
      <Card className="space-y-2.5 px-4 py-4">
        <Input label="规格名" placeholder="标准装" value={spec} onChange={(e) => setSpec(e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <Input
            label={isSupplier ? "供货价（元）" : "我卖多少（元）"}
            inputMode="decimal"
            placeholder="0.00"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
          <Input
            label="库存"
            inputMode="numeric"
            placeholder="0"
            value={stock}
            onChange={(e) => setStock(e.target.value)}
          />
        </div>
        <Input label="单位" placeholder="件 / 片 / 平方米" value={unit} onChange={(e) => setUnit(e.target.value)} />
        {/*
          ⚠️ 一句话说清这个数是什么。供应商填错（把零售价当供货价）
             会让全链的价当场翻倍，而这个错要到有人下单才看得出来。
        */}
        <p className="text-[12px] leading-relaxed text-muted-foreground">
          {isSupplier
            ? "这是你给下游的价，各级会在它之上加自己的差价。对下游永远不可见。"
            : "这就是客户在你店里看到的价。"}
        </p>
      </Card>

      {/*
        ── 商品详情图 ──
        🔴 **跟商品图是两回事**：商品图是"长什么样"（列表卡、订单缩略图都取它，
           得是方图）；详情图是"介绍商品"的长图，只排在详情页最下面。
        ⚠️ 摆在规格后面（老板指定）+ **整张铺开** —— 这一格就是详情页的预览，
           传完立刻看到客户会看到什么。手机屏本来就是 750 那个宽度，
           铺开之后跟真实详情页几乎一模一样。
      */}
      <SectionTitle>商品详情图（选填）</SectionTitle>
      <Card className="px-3 py-3">
        <p className="mb-2 text-[12px] text-muted-foreground">
          下面就是客户在详情页最下面看到的样子，从上往下排。
        </p>
        <ImageUpload
          value={detailKeys}
          onChange={setDetailKeys}
          scope="product"
          orgId={orgId}
          max={20}
          label="添加详情图"
          variant="strip"
        />
      </Card>

      {create.error && <ErrorBlock error={create.error} />}

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onDone}>
          取消
        </Button>
        <Button
          className="flex-1"
          loading={create.isPending}
          disabled={!ready}
          onClick={() => create.mutate()}
        >
          提交审核
        </Button>
      </div>
      <p className="text-center text-[12px] text-muted-foreground">
        提交后由平台审核，通过就会出现在你店里。
      </p>
    </div>
  );
}


export interface SkuRow {
  id: number;
  specs: Record<string, string>;
  supplyPrice: number;
  listPrice: number | null;
  stock: number;
  unit: string;
}


export interface ProductDetail {
  id: number;
  title: string;
  status: string;
  imageKeys: string[];
  /** 详情图 —— 详情页最下面那一长条。⚠️ 跟 `imageKeys` 是两回事，见表单上的说明。 */
  detailImageKeys: string[];
  skus: SkuRow[];
}

/**
 * 改一件已经上架的商品。
 *
 * 🔴 **上架之后最常做的三件事：改价、补库存、下架。**
 *    只能新建不能改的话，价格一变就只能重新上一件 ——
 *    老的还挂在客户的收藏里，新的没有销量，而且两件重复的货谁也分不清。
 *
 * ⚠️ 库存用**相对增减**不是设值：两个人同时补货时"设成 100"会互相覆盖，
 *    "各加 50"才是对的。
 */
function EditProduct({
  orgId,
  orgType,
  productId,
  categories,
  onDone,
}: {
  orgId: number;
  /** 见 `NewProduct` 的说明：同一个字段两种身份含义完全不同。 */
  orgType: string;
  productId: number;
  categories: { id: number; name: string }[];
  onDone: () => void;
}) {
  const isSupplier = orgType === "supplier" || orgType === "platform";
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ["merchant", orgId, "product", productId],
    queryFn: () => orgFetch<ProductDetail>(`/products/${productId}`, orgId),
  });
  const myCat = useQuery({
    queryKey: ["merchant", orgId, "shop-categories"],
    queryFn: () => orgFetch<{ id: number; name: string }[]>("/shop/categories", orgId),
  });
  const [title, setTitle] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [detailImages, setDetailImages] = useState<string[]>([]);
  const [prices, setPrices] = useState<Record<number, string>>({});
  const [addStock, setAddStock] = useState<Record<number, string>>({});
  const [shopCat, setShopCat] = useState<string>("");
  const [loaded, setLoaded] = useState(false);

  /** ⚠️ 数据回来之后灌一次初值 —— 只在 useState 里给的话输入框永远是空的。 */
  useEffect(() => {
    if (!detail.data || loaded) return;
    setTitle(detail.data.title);
    setImages(detail.data.imageKeys ?? []);
    setDetailImages(detail.data.detailImageKeys ?? []);
    setPrices(
      Object.fromEntries(
        detail.data.skus.map((k) => [k.id, String(+(k.supplyPrice / 100).toFixed(2))]),
      ),
    );
    setLoaded(true);
  }, [detail.data, loaded]);

  const saveInfo = useMutation({
    mutationFn: () =>
      orgFetch(`/products/${productId}`, orgId, {
        method: "PATCH",
        body: JSON.stringify({
          title: title.trim(),
          imageKeys: images,
          detailImageKeys: detailImages,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  const savePrice = useMutation({
    mutationFn: async (v: { skuId: number; yuan: string }) => {
      const cents = Math.round(Number(v.yuan) * 100);
      await orgFetch(`/skus/${v.skuId}`, orgId, {
        method: "PATCH",
        body: JSON.stringify({ supplyPrice: cents }),
      });
      await orgFetch("/pricing/sku", orgId, {
        method: "POST",
        body: JSON.stringify({ skuId: v.skuId, price: cents }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  const restock = useMutation({
    mutationFn: (v: { skuId: number; delta: number }) =>
      orgFetch(`/skus/${v.skuId}/stock`, orgId, {
        method: "POST",
        body: JSON.stringify({ delta: v.delta }),
      }),
    onSuccess: (_d, v) => {
      setAddStock((m) => ({ ...m, [v.skuId]: "" }));
      void qc.invalidateQueries({ queryKey: ["merchant", orgId] });
    },
  });

  /**
   * 🔴 **待审核的商品改不了**（审核员看到的必须就是最终上架的那份）——
   *    所以要给一条"撤回"的路。没有它的话，提交之后发现写错字就再也改不了了，
   *    只能干等平台审完，而审核队列里挂的就是那份错的。
   */
  const withdraw = useMutation({
    mutationFn: () => orgFetch(`/products/${productId}/withdraw`, orgId, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  const submit = useMutation({
    mutationFn: () => orgFetch(`/products/${productId}/submit`, orgId, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  const toggle = useMutation({
    mutationFn: (online: boolean) =>
      orgFetch(`/products/${productId}/${online ? "online" : "offline"}`, orgId, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  const setCategory = useMutation({
    mutationFn: (categoryId: number | null) =>
      orgFetch(`/shop/products/${productId}/category`, orgId, {
        method: "POST",
        body: JSON.stringify({ categoryId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["merchant", orgId] }),
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error) return <ErrorBlock error={detail.error} />;
  const d = detail.data!;

  const pending = d.status === "pending";

  return (
    <div className="space-y-3 px-3 py-3 pb-8">
      {/*
        🔴 **状态决定这一页能干什么，要摆在最上面说清楚。**
           不说的话他改半天点保存，收到一句"商品正在审核中，不能修改" ——
           而那时候他填的东西已经白填了。
      */}
      {pending && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-800">
          正在等平台审核，这期间改不了 —— 审核员看到的必须就是最终上架的那份。
          要改先撤回。
          <Button
            size="sm"
            variant="outline"
            className="mt-2 w-full"
            loading={withdraw.isPending}
            onClick={() => withdraw.mutate()}
          >
            撤回审核（回到草稿）
          </Button>
          {withdraw.error && <ErrorBlock error={withdraw.error} />}
        </div>
      )}

      {d.status === "rejected" && (
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-[13px] leading-relaxed text-destructive">
          平台驳回了这件商品。改完之后重新提交审核。
        </div>
      )}

      <SectionTitle>商品信息</SectionTitle>
      <Card className="space-y-2.5 px-4 py-4">
        <Input label="商品名称" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div>
          <span className="mb-1.5 block text-[13px] text-muted-foreground">商品图</span>
          <ImageUpload value={images} onChange={setImages} scope="product" orgId={orgId} max={6} />
        </div>
        {saveInfo.error && <ErrorBlock error={saveInfo.error} />}
        <Button
          className="w-full"
          loading={saveInfo.isPending}
          disabled={title.trim().length < 2 || pending}
          onClick={() => saveInfo.mutate()}
        >
          {pending ? "审核中，改不了" : "保存"}
        </Button>
      </Card>

      <SectionTitle>规格</SectionTitle>
      {d.skus.map((k) => {
        return (
        <Card key={k.id} className="space-y-2.5 px-4 py-4">
          <p className="text-[14px]">{Object.values(k.specs).join(" / ") || "标准装"}</p>

          {/*
            🔴 **只有一个价：我卖多少。**
               货是他自己的，"进货成本"这个概念对他不成立（他在系统外把货弄来的）——
               这个数就是客户在他店里看到的价，同时也是他下游进货的价。
               早先这里摆了「成本」和「售价」两个框，而成本是整条链的起点，
               他很容易顺手把下游的进货价一起改了。
          */}
          <div className="flex items-end gap-2">
            <Input
              label={isSupplier ? "供货价（元）" : "我卖多少（元）"}
              inputMode="decimal"
              className="flex-1"
              value={prices[k.id] ?? ""}
              onChange={(e) => setPrices((m) => ({ ...m, [k.id]: e.target.value }))}
            />
            <Button
              size="sm"
              loading={savePrice.isPending}
              disabled={!Number(prices[k.id]) || pending}
              onClick={() => savePrice.mutate({ skuId: k.id, yuan: prices[k.id] ?? "0" })}
            >
              改价
            </Button>
          </div>
          <p className="text-[12px] leading-relaxed text-muted-foreground">
            {isSupplier
              ? "这是你给下游的价，各级会在它之上加自己的差价。"
              : "这就是客户在你店里看到的价。"}
          </p>

          <div className="flex items-end gap-2">
            <Input
              label={`补库存（当前 ${k.stock} ${k.unit}）`}
              inputMode="numeric"
              className="flex-1"
              placeholder="填要增加的数量"
              value={addStock[k.id] ?? ""}
              onChange={(e) => setAddStock((m) => ({ ...m, [k.id]: e.target.value }))}
            />
            <Button
              size="sm"
              variant="outline"
              loading={restock.isPending}
              disabled={!Number(addStock[k.id])}
              onClick={() => restock.mutate({ skuId: k.id, delta: Number(addStock[k.id]) })}
            >
              入库
            </Button>
          </div>
          {/*
            ⚠️ 说清楚是"增加"不是"设成" —— 写成设值的话两个人同时补货会互相覆盖，
               而且少的那部分谁也发现不了。
          */}
          <p className="text-[12px] text-muted-foreground">
            填的是<strong>增加</strong>多少，不是改成多少。退库存填负数。
          </p>
          {(savePrice.error || restock.error) && (
            <ErrorBlock error={savePrice.error ?? restock.error} />
          )}
        </Card>
        );
      })}

      {/*
        ── 商品详情图 ──
        🔴 跟商品图是两回事，见新建表单上的说明。
        ⚠️ 摆在规格后面 + **整张铺开**：这一格就是详情页的预览。
      */}
      <SectionTitle>商品详情图</SectionTitle>
      <Card className="px-3 py-3">
        <p className="mb-2 text-[12px] text-muted-foreground">
          下面就是客户在详情页最下面看到的样子，从上往下排。
        </p>
        <ImageUpload
          value={detailImages}
          onChange={setDetailImages}
          scope="product"
          orgId={orgId}
          max={20}
          label="添加详情图"
          variant="strip"
        />
        <Button
          className="mt-3 w-full"
          loading={saveInfo.isPending}
          disabled={pending}
          onClick={() => saveInfo.mutate()}
        >
          {pending ? "审核中，改不了" : "保存详情图"}
        </Button>
      </Card>

      <SectionTitle>放进我店里的哪个货架</SectionTitle>
      <Card className="px-4 py-4">
        <select
          className="w-full rounded-lg border border-border bg-card px-3 py-2.5 text-[16px]"
          value={shopCat}
          onChange={(e) => {
            setShopCat(e.target.value);
            setCategory.mutate(e.target.value ? Number(e.target.value) : null);
          }}
        >
          <option value="">未分类</option>
          {(myCat.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
          {/* ⚠️ 说明这是店铺货架不是全站品类，两个概念长得太像。 */}
          客户进你的店时按这个分组看商品。跟平台品类（{categories.map((c) => c.name).slice(0, 3).join(" / ")}…）
          是两回事。
        </p>
        {setCategory.error && <ErrorBlock error={setCategory.error} />}
      </Card>

      <SectionTitle>上下架</SectionTitle>
      <Card className="px-4 py-4">
        {/*
          🔴 下架之后**客户立刻看不到**，但已经下过的单不受影响
             （那些按订单快照走）—— 不说的话他不敢下架。
        */}
        <p className="mb-3 text-[13px] leading-relaxed text-muted-foreground">
          下架后客户立刻看不到这件商品。<strong>已经下过的单不受影响</strong>，该发的货照发。
        </p>
        {toggle.error && <ErrorBlock error={toggle.error} />}
        {/*
          ⚠️ 三种状态三个动作，别用一个按钮硬撑：
             在架 → 下架 · 已下架 → 重新上架（不用重审）· 草稿/驳回 → 提交审核。
        */}
        {d.status === "active" && (
          <Button
            variant="outline"
            className="w-full"
            loading={toggle.isPending}
            onClick={() => toggle.mutate(false)}
          >
            下架
          </Button>
        )}
        {d.status === "offline" && (
          <Button className="w-full" loading={toggle.isPending} onClick={() => toggle.mutate(true)}>
            重新上架
          </Button>
        )}
        {(d.status === "draft" || d.status === "rejected") && (
          <Button className="w-full" loading={submit.isPending} onClick={() => submit.mutate()}>
            提交审核
          </Button>
        )}
        {submit.error && <ErrorBlock error={submit.error} />}
      </Card>

      <Button variant="ghost" className="w-full" onClick={onDone}>
        返回列表
      </Button>
    </div>
  );
}

function MyProducts() {
  const search = useSearch({ from: "/my-products" }) as { orgId?: number };
  const { data: identities, isPending } = useIdentities();
  const qc = useQueryClient();
  const orgs = identities?.orgs ?? [];
  const orgId = search.orgId ?? orgs[0]?.id;
  const myOrg = orgs.find((o) => o.id === orgId);
  const isSupplier = myOrg?.type === "supplier" || myOrg?.type === "platform";

  const [creating, setCreating] = useState(false);
  /** 正在编辑哪一件。⚠️ 整页换掉而不是展开 —— 手机上展开会让列表长到找不着北。 */
  const [editingId, setEditingId] = useState<number | null>(null);
  const { data: platformCats } = useCategories();

  const list = useQuery({
    queryKey: ["merchant", orgId, "my-products"],
    queryFn: () => orgFetch<{ items: MyProduct[] }>("/products?limit=50", orgId!),
    enabled: Boolean(orgId),
  });

  /**
   * 🔴 **没有商家身份时不能只转圈。**
   *    下面那个查询在 `orgId` 为空时是 disabled 的，而 disabled 的查询
   *    `isPending` 恒为 true —— 未登录、或登录了但名下没有店的人，
   *    看到的是一个**永远停不下来的圈**，页面上没有任何提示也没有出路。
   */
  if (!isPending && !orgId) {
    return (
      <SubPage title="我的商品">
        <Empty title="你还不是商家" hint="这是商家功能。想开店请联系上级商家或平台。" action={{ label: "回首页", to: "/" }} />
      </SubPage>
    );
  }
  if (isPending || list.isPending) return <SubPage title="我的商品"><Spinner /></SubPage>;
  if (list.error) return <SubPage title="我的商品"><ErrorBlock error={list.error} /></SubPage>;

  if (editingId && orgId) {
    return (
      <SubPage title="编辑商品">
        <EditProduct
          orgId={orgId}
          orgType={myOrg?.type ?? ""}
          productId={editingId}
          categories={platformCats ?? []}
          onDone={() => setEditingId(null)}
        />
      </SubPage>
    );
  }

  if (creating && orgId) {
    return (
      <SubPage title="上架商品">
        <NewProduct
          orgId={orgId}
          orgType={myOrg?.type ?? ""}
          onDone={() => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: ["merchant", orgId, "my-products"] });
          }}
        />
      </SubPage>
    );
  }

  const items = list.data?.items ?? [];

  return (
    <SubPage
      title="我的商品"
      footer={
        <Button className="w-full" onClick={() => setCreating(true)}>
          <Plus className="size-4" />
          上架我自己的商品
        </Button>
      }
    >
      {items.length === 0 ? (
        <Empty
          title="还没有自己的商品"
          hint="自己进的辅料、自己做的定制柜都可以传上来卖，价格从你这一级起算"
        />
      ) : (
        <div className="space-y-2.5 px-3 py-3">
          {items.map((p) => (
            <Card
              key={p.id}
              className="flex cursor-pointer gap-3 px-3 py-3 active:bg-secondary"
              onClick={() => setEditingId(p.id)}
            >
              <Img
                src={p.imageUrls?.[0]}
                alt={p.title}
                className="size-20 shrink-0 rounded-lg object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-[14px]">{p.title}</p>
                <div className="mt-1.5 flex items-center gap-2">
                  <Badge variant={STATUS_TONE[p.status] ?? "secondary"}>
                    {STATUS_LABEL[p.status] ?? p.status}
                  </Badge>
                  {p.skuCount != null && (
                    <span className="text-[12px] text-muted-foreground">{p.skuCount} 个规格</span>
                  )}
                </div>
                {p.cost != null && (
                  <p className="mt-1 text-[13px] text-muted-foreground">
                    {isSupplier ? "供货价 " : "售价 "}
                    <Price cents={p.cost} />
                    {p.stock != null && <span className="ml-2">库存 {p.stock}</span>}
                  </p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function MyProductsRoute() {
  return useIsDesktop() ? <DesktopMyProducts /> : <MyProducts />;
}

export const myProductsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my-products",
  component: MyProductsRoute,
  validateSearch: (s: Record<string, unknown>): { orgId?: number } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
  }),
});
